import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Card } from '@material-ui/core';
import { Info as InfoIcon } from '@material-ui/icons';

import { FetchError } from '../../../common/api/api';
import CardTitle from '../../../common/card/CardTitle';
import CardContent from '../../../common/card/CardContent';
import InlineForm from '../../../common/inline/InlineForm';
import InlineHtml from '../../../common/inline/InlineHtml';
import InlineFormik from '../../../common/inline/InlineFormik';
import { InlineFileValue } from '../../../common/inline/InlineFile';
import InlineTextField from '../../../common/inline/InlineTextField';
import { useLinkTransform } from '../../../common/HeaderBackButton';
import { useUpdateTask } from '../../tasks/Task.hooks';
import { useUpdateProductVerification } from '../Product.hooks';
import {
  ProductVerification,
  BaseProductVerificationCardProps
} from '../Product.interface';
import { useTaskEntityFields } from '../../workflow/Workflow.hooks';
import { getProductEntityName } from '../Product.config';

const VerificationInfoCard: React.FC<BaseProductVerificationCardProps> = (
  props: BaseProductVerificationCardProps
) => {
  const {
    title = `Product ${getProductEntityName('verification')} Info`,
    productId,
    productVerification,
    productVerificationVersion,
    hidden = false,
    enableTitleRedirect = false,
    onChange,
    onUpdate,
    onError
  } = props;
  const initialData = productVerificationVersion || productVerification;
  const initialValues: Partial<ProductVerification> = {
    title: initialData.title || '',
    description: initialData.description || ''
  };
  const getPath = useLinkTransform(
    `/portal/products/${productId}/verifications/${productVerification.id}`,
    [{ query: 'from' }]
  );
  const {
    updatedProductVerification,
    isUpdatingProductVerification: isUpdating,
    updateProductVerificationError: updateError,
    updateProductVerification
  } = useUpdateProductVerification(productId, productVerification.id);
  const {
    updatedTask,
    updateError: updateTaskError,
    updateTask
  } = useUpdateTask(productVerification.task.id || 0);
  const error: FetchError | undefined = updateError || updateTaskError;
  const { isFieldDisabled } = useTaskEntityFields(productVerification.task);

  useEffect(() => {
    if (updatedProductVerification) {
      onChange?.(updatedProductVerification);
      onUpdate?.();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [updatedProductVerification]);

  useEffect(() => {
    if (updatedTask) {
      productVerification.task = Object.assign(
        productVerification.task || {},
        updatedTask
      );
      onChange?.(productVerification);
      onUpdate?.();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [updatedTask]);

  useEffect(() => {
    if (error) {
      onError?.(error);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [error]);

  return (
    <Card style={hidden ? { display: 'none' } : undefined}>
      <CardTitle>
        <InfoIcon />
        {enableTitleRedirect ? (
          <Link className='link' to={getPath()}>
            {title}
          </Link>
        ) : (
          title
        )}
      </CardTitle>
      <CardContent padding='large'>
        <InlineFormik
          error={updateError}
          isSubmitting={isUpdating}
          initialValues={initialValues}
          onSubmit={(values: Partial<ProductVerification>) => {
            updateProductVerification(values);
            onChange?.(values);
          }}
        >
          <InlineForm>
            <InlineTextField
              disabled
              name='productId'
              label='Product ID'
              value={`PRODUCT-${productId}`}
            />
            <InlineTextField
              name='title'
              label='Title'
              placeholder={`Enter title for Product ${getProductEntityName(
                'verification'
              )}`}
              disabled={
                Boolean(productVerificationVersion) ||
                isUpdating ||
                isFieldDisabled('title')
              }
            />
            <InlineHtml
              name='description'
              label='Description'
              helperText={`Enter description for Product ${getProductEntityName(
                'verification'
              )}`}
              disabled={
                Boolean(productVerificationVersion) ||
                isUpdating ||
                isFieldDisabled('description')
              }
              attachments={productVerification.task.attachments || []}
              onChangeAttachments={(attachments: InlineFileValue[]) => {
                updateTask({ attachments });
                if (productVerification.task) {
                  productVerification.task.attachments = attachments;
                }
                onChange?.(productVerification);
              }}
            />
          </InlineForm>
        </InlineFormik>
      </CardContent>
    </Card>
  );
};

export default VerificationInfoCard;
